import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  CheckCircle2,
  CircleAlert,
  KeyRound,
  Loader2,
  Pencil,
  RefreshCw,
  ScanLine,
  Smartphone,
  Wifi,
  X,
} from 'lucide-react';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { devicesApi, networkApi, wirelessApi } from '@/lib/api';
import { useGlobalPrefs, useGlobalPrefsUpdate } from '@/lib/myPrefs';
import type {
  PairingSession,
  PairingTraceEntry,
  PairingTraceStage,
  WirelessPairState,
} from '@/lib/types';

/**
 * Modal for adding a wireless ADB device. Two flows live behind tabs:
 *
 *   - **快速** uses a currently USB-connected device to remotely launch
 *     the Android "Pair with QR code" Settings activity (via the server-
 *     side OEM intent catalogue). The user only has to scan the QR — no
 *     menu spelunking. Best path when the phone is on a desk next to a
 *     test PC.
 *
 *   - **手动** assumes the user already has the wireless debugging
 *     screen open on the phone; they just scan the QR. Useful when the
 *     phone has never been USB-paired with this server.
 *
 * Both tabs hit the same `/api/wireless/pair/start` endpoint; only
 * `launchOnSerial` differs. The session is polled every 1s and the
 * modal advances through awaiting_scan → pairing → paired → connected.
 */
export function AddWirelessDebugModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="card relative w-full max-w-2xl overflow-hidden">
        <header className="flex items-center justify-between border-b border-line bg-bg-soft px-4 py-3">
          <div className="flex items-center gap-2">
            <Wifi className="h-4 w-4 text-accent" />
            <h2 className="text-sm font-semibold tracking-tight">添加无线设备 · Pair over Wi-Fi</h2>
          </div>
          <button className="btn-ghost h-7 w-7 justify-center p-0" onClick={onClose} title="关闭">
            <X className="h-4 w-4" />
          </button>
        </header>
        <Body onDone={onClose} />
      </div>
    </div>
  );
}

/**
 * Two pairing flows live behind tabs (May 2026 — merged the old
 * `oneclick` + `qr` tabs because they're functionally the same: both
 * end with the user scanning a QR. The only difference was whether
 * the server pushed an `am start` to a USB phone first, and that's
 * now an optional control inside the merged tab):
 *
 *   - `qr` (Tab A · 默认): auto-generates a QR the moment the tab
 *     opens. An optional "推送扫码页 →" button uses a currently-USB
 *     phone to remotely launch 「无线调试 → 通过二维码配对」 (handy
 *     when the phone is on a test bench). The QR auto-refreshes when
 *     the user picks a different target USB phone.
 *   - `manualcode` (Tab B): user reads `host:port` + 6-digit code off
 *     the phone's 「通过配对码配对设备」 page; we run `adb pair` +
 *     `adb connect` synchronously, bypassing mDNS entirely. Last-
 *     resort fallback for macOS multi-NIC / corporate Wi-Fi where
 *     mDNS is silent.
 */
type TabKey = 'qr' | 'manualcode';

function Body({ onDone }: { onDone: () => void }) {
  const [tab, setTab] = useState<TabKey>('qr');
  // Optional `am start` target — when set, the server pushes the
  // 「通过二维码配对」 page to this USB phone right after generating
  // the QR. Empty string = skip the push, just show the QR.
  const [launchOnSerial, setLaunchOnSerial] = useState<string>('');
  const [session, setSession] = useState<PairingSession | null>(null);
  const startedRef = useRef(false);

  // Pull the device list for both tabs — QR uses it to drive the
  // "push to phone" picker, and the manual tab reads the picked USB
  // phone's Wi-Fi IP to pre-fill 「手机 IP」. Filter to online USB
  // devices — only those can serve as either an `am start` target or
  // an `adb shell dumpsys wifi` source.
  const { data: devicesData } = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.list(),
    refetchInterval: 10_000,
  });
  const usbCandidates = useMemo(() => {
    return (devicesData?.devices ?? []).filter(
      (d) => d.kind === 'usb' && d.state === 'device'
    );
  }, [devicesData]);

  // Auto-pick the first USB candidate when none chosen yet so both
  // the network-context banner and the manual-tab IP auto-fill have
  // something to work with from the moment the modal opens.
  useEffect(() => {
    if (!launchOnSerial && usbCandidates.length > 0) {
      setLaunchOnSerial(usbCandidates[0].serial);
    }
  }, [launchOnSerial, usbCandidates]);

  /**
   * Kick off a QR pair session.
   *
   * `pushToPhone === true` (default for the post-mount auto-start and
   * the explicit "推送扫码页" button) → sends `launchOnSerial` so the
   * server first lands an `am start` on the picked phone. Falls back
   * gracefully when the OEM intent fails — the QR still works.
   *
   * Always cancels any in-flight session first so flipping the target
   * USB phone gets a fresh QR instead of stacking sessions.
   */
  async function startQrPair(pushToPhone: boolean = true): Promise<void> {
    if (startedRef.current) return;
    startedRef.current = true;
    // Cancel any prior session — fire-and-forget; the server-side
    // cleanup is idempotent and we don't want to block the new
    // session on the old one's HTTP round-trip.
    if (session && !isTerminal(session.state) && session.sessionId) {
      const id = session.sessionId;
      void wirelessApi.cancel(id).catch(() => undefined);
    }
    try {
      const opts = pushToPhone && launchOnSerial ? { launchOnSerial } : {};
      const r = await wirelessApi.startPair(opts);
      setSession(r.session);
    } catch (e) {
      setSession({
        sessionId: '',
        state: 'failed',
        service: { name: '', password: '' },
        qrPayload: '',
        qrDataUrl: '',
        pairCode: '',
        createdAt: Date.now(),
        expiresAt: Date.now(),
        error: (e as Error).message,
        trace: [],
      });
    } finally {
      startedRef.current = false;
    }
  }

  async function startManualPair(input: {
    host: string;
    port: number;
    connectPort?: number;
    code: string;
  }): Promise<void> {
    if (startedRef.current) return;
    startedRef.current = true;
    try {
      const r = await wirelessApi.pairManual(input);
      setSession(r.session);
    } catch (e) {
      setSession({
        sessionId: '',
        state: 'failed',
        service: { name: '', password: '' },
        qrPayload: '',
        qrDataUrl: '',
        pairCode: '',
        createdAt: Date.now(),
        expiresAt: Date.now(),
        error: (e as Error).message,
        trace: [],
      });
    } finally {
      startedRef.current = false;
    }
  }

  // Auto-generate the QR the moment the QR tab is active and there's
  // no live session. Also auto-refresh when the user picks a different
  // USB target — the new pair-page push goes to the new phone and the
  // old QR is replaced so the user doesn't end up scanning a stale one.
  // The manual tab is intentionally excluded — `adb pair` only runs
  // after the user types host/port/code.
  useEffect(() => {
    if (tab !== 'qr') return;
    if (session) return;
    void startQrPair(true);
    // launchOnSerial dependency: when the user picks a different USB
    // phone, fall through (session is still null at this point only
    // on first mount; otherwise the explicit re-trigger below handles
    // it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Refresh QR when the user switches the target USB device while the
  // QR tab is open and the session hasn't reached a terminal state.
  // Cancels the existing session then starts a new one so the QR is
  // tied to the freshly-picked phone.
  const prevLaunchOnSerialRef = useRef(launchOnSerial);
  useEffect(() => {
    if (tab !== 'qr') {
      prevLaunchOnSerialRef.current = launchOnSerial;
      return;
    }
    if (prevLaunchOnSerialRef.current === launchOnSerial) return;
    prevLaunchOnSerialRef.current = launchOnSerial;
    // Only restart while the QR is still pending — don't blow away a
    // session that's already pairing / connected just because the
    // user fiddled with the dropdown.
    if (session && !(session.state === 'awaiting_scan' || session.state === 'failed')) {
      return;
    }
    void startQrPair(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [launchOnSerial, tab]);

  // Poll every 1s once a session is live, until terminal state.
  useEffect(() => {
    if (!session || isTerminal(session.state)) return;
    const id = session.sessionId;
    if (!id) return;
    let cancelled = false;
    const tick = async (): Promise<void> => {
      try {
        const r = await wirelessApi.status(id);
        if (cancelled) return;
        setSession(r.session);
      } catch (e) {
        if (cancelled) return;
        setSession((prev) =>
          prev
            ? { ...prev, state: 'failed', error: (e as Error).message }
            : prev
        );
      }
    };
    const handle = window.setInterval(() => void tick(), 1000);
    return (): void => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [session]);

  async function reset(): Promise<void> {
    if (session && !isTerminal(session.state) && session.sessionId) {
      try {
        await wirelessApi.cancel(session.sessionId);
      } catch {
        /* best-effort */
      }
    }
    setSession(null);
  }

  return (
    <div className="space-y-3 p-4">
      {/*
        Network-context panel surfaces the hub's own Wi-Fi (SSID +
        CIDR) and, when a USB phone is in the picker, that phone's
        Wi-Fi too. Shared across BOTH tabs (May 2026) — the SSID/IP
        info is just as relevant when typing host:port manually as
        when scanning a QR, and stacking it at the top of the modal
        avoids re-rendering / re-fetching when the user flips tabs.
      */}
      {!session && <NetworkContextPanel usbSerial={launchOnSerial || undefined} />}

      {/*
        mDNS reachability pre-flight. Server probes the LAN for ~3s
        when the modal mounts; if no `_adb-tls-pairing._tcp` advert
        arrives, we KNOW the QR-pair flow will silently time out
        (corporate Wi-Fi client isolation, VPN default-route, IGMP
        snooping…). Surfaced as an amber callout with a one-click
        "切到手动输入" so the user pivots before scanning. Stays
        rendered across both tabs — it's network-state, not
        flow-state.
      */}
      {!session && (
        <MdnsHealthBanner
          showSwitch={tab === 'qr'}
          onSwitchToManual={() => setTab('manualcode')}
        />
      )}

      {/* Tabs */}
      {!session && (
        <div className="flex gap-1 rounded-md bg-bg-soft p-1 text-xs">
          <TabBtn active={tab === 'qr'} onClick={() => setTab('qr')}>
            <ScanLine className="h-3.5 w-3.5" /> 扫码配对 · QR Pair
          </TabBtn>
          <TabBtn active={tab === 'manualcode'} onClick={() => setTab('manualcode')}>
            <KeyRound className="h-3.5 w-3.5" /> 手动输入 · host:port + code
          </TabBtn>
        </div>
      )}

      {!session && tab === 'qr' && (
        <QrPairPanel
          usbDevices={usbCandidates}
          selected={launchOnSerial}
          onSelect={setLaunchOnSerial}
          onPushPairPage={() => void startQrPair(true)}
          onRefreshQr={() => void startQrPair(false)}
        />
      )}
      {!session && tab === 'manualcode' && (
        <ManualCodePanel
          onStart={startManualPair}
          usbSerial={launchOnSerial || undefined}
        />
      )}

      {session && (
        <SessionPanel
          session={session}
          usbDevices={usbCandidates}
          launchOnSerial={launchOnSerial}
          onSelectLaunchSerial={setLaunchOnSerial}
          onPushPairPage={() => void startQrPair(true)}
          onRefreshQr={() => void startQrPair(false)}
          onSwitchToManual={async () => {
            await reset();
            setTab('manualcode');
          }}
          onCancel={reset}
          onClose={onDone}
        />
      )}
    </div>
  );
}

/**
 * Tab C body: free-form host / port / 6-digit-code entry. Use case is
 * "mDNS is silent" — macOS with multiple NICs that bonjour-service
 * didn't bind to, corporate Wi-Fi with client-isolation / IGMP
 * snooping, or any other scenario where `_adb-tls-pairing._tcp` never
 * shows up on the hub. The user reads the three values straight off
 * 「开发者选项 → 无线调试 → 通过配对码配对设备」 and we run
 * `adb pair` + `adb connect` directly.
 */
function ManualCodePanel({
  onStart,
  usbSerial,
}: {
  onStart: (v: { host: string; port: number; connectPort?: number; code: string }) => void;
  /**
   * When the user picked a USB phone in the QR tab's device dropdown,
   * we re-use that selection here to pre-fill 「手机 IP」 from the
   * phone's `dumpsys wifi`. Saves a manual transcription step and
   * makes the manual flow the one-and-only fallback when mDNS is
   * dead — exactly the path this UI is meant for.
   */
  usbSerial?: string;
}) {
  // Auto-detect the phone IP via the already-tethered USB device.
  // Best-effort: when no USB device is selected, or the phone has
  // Wi-Fi off, the query no-ops and the user types the IP themselves.
  const phone = useQuery({
    queryKey: ['device.network', usbSerial],
    queryFn: () => devicesApi.network(usbSerial!),
    enabled: !!usbSerial,
    staleTime: 15_000,
    retry: false,
  });
  const detectedIp = phone.data?.primary?.ipv4;

  const [host, setHost] = useState('');
  // Tracks whether the user has manually edited the host field. We
  // only auto-fill the detected IP while the field is still untouched
  // — once the user types something, we never overwrite it.
  const [hostEdited, setHostEdited] = useState(false);
  useEffect(() => {
    if (!hostEdited && detectedIp && host === '') {
      setHost(detectedIp);
    }
  }, [detectedIp, hostEdited, host]);

  const [port, setPort] = useState('');
  // REQUIRED (May 2026). Android exposes a persistent connect port
  // shown next to "IP 与端口" on the main 无线调试 screen, distinct
  // from the ephemeral pair port. It used to be "optional" with a
  // mDNS-discovery fallback — but on networks where the user reaches
  // this tab (mDNS dead), the fallback is guaranteed to fail. So we
  // demand the connect port up-front instead of wasting an 8s probe.
  const [connectPort, setConnectPort] = useState('');
  const [code, setCode] = useState('');
  const hostValid = /^[\w.:[\]-]{1,128}$/.test(host.trim());
  const portNum = Number.parseInt(port.trim(), 10);
  const portValid = Number.isInteger(portNum) && portNum > 0 && portNum <= 65535;
  const connectPortNum = Number.parseInt(connectPort.trim(), 10);
  const connectPortValid =
    Number.isInteger(connectPortNum) && connectPortNum > 0 && connectPortNum <= 65535;
  const codeValid = /^\d{6}$/.test(code.trim());
  const allValid = hostValid && portValid && connectPortValid && codeValid;
  return (
    <div className="space-y-3">
      <ol className="list-decimal space-y-1 pl-5 text-xs text-ink">
        <li>
          手机进入 <span className="font-mono">设置 → 开发者选项 → 无线调试</span>。
        </li>
        <li>
          记下页面顶部「<span className="font-mono">IP 与端口</span>」一行 — 这是
          <strong className="font-medium">连接端口</strong>（长期不变），下面要填。
        </li>
        <li>
          点击 <span className="font-mono">通过配对码配对设备</span>，把弹出页面的
          <span className="font-mono">IP 地址和端口</span>（<strong className="font-medium">配对端口</strong>，每次进都会变）
          与 <span className="font-mono">Wi-Fi 配对码</span> 填到下面。
        </li>
      </ol>
      <div className="grid grid-cols-12 gap-2">
        <label className="col-span-7 text-xs">
          <span className="mb-1 flex items-center justify-between text-[11px] uppercase tracking-wide text-ink-dim">
            <span>手机 IP（host）</span>
            {detectedIp && host === detectedIp && !hostEdited && (
              <span
                className="normal-case text-emerald-600 dark:text-emerald-400"
                title={`通过 USB 设备 ${usbSerial} 自动检测到`}
              >
                ✓ 已自动填充
              </span>
            )}
          </span>
          <input
            className="input w-full font-mono"
            placeholder={detectedIp ? `自动: ${detectedIp}` : '192.168.1.50'}
            value={host}
            onChange={(e) => {
              setHost(e.target.value.trim());
              setHostEdited(true);
            }}
            autoFocus
          />
        </label>
        <label className="col-span-5 text-xs">
          <span className="mb-1 block text-[11px] uppercase tracking-wide text-ink-dim">
            配对端口（pair port）
          </span>
          <input
            className="input w-full font-mono"
            placeholder="43173"
            inputMode="numeric"
            value={port}
            onChange={(e) => setPort(e.target.value.trim())}
          />
        </label>
        <label className="col-span-7 text-xs">
          <span className="mb-1 block text-[11px] uppercase tracking-wide text-ink-dim">
            6 位配对码（pair code）
          </span>
          <input
            className="input w-full font-mono tracking-widest"
            placeholder="123456"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D+/g, '').slice(0, 6))}
          />
        </label>
        <label className="col-span-5 text-xs">
          <span className="mb-1 block text-[11px] uppercase tracking-wide text-ink-dim">
            连接端口（connect port）
          </span>
          <input
            className="input w-full font-mono"
            placeholder="39201"
            inputMode="numeric"
            value={connectPort}
            onChange={(e) => setConnectPort(e.target.value.trim())}
          />
        </label>
      </div>
      <button
        className="btn-primary w-full"
        disabled={!allValid}
        onClick={() =>
          onStart({
            host: host.trim(),
            port: portNum,
            connectPort: connectPortNum,
            code: code.trim(),
          })
        }
      >
        <KeyRound className="h-4 w-4" /> 直连配对（绕过 mDNS）
      </button>
      <p className="text-[11px] text-ink-dim">
        提示：
        <strong className="font-medium">配对端口</strong>每次进入「通过配对码配对设备」都会变（要尽快输入）；
        <strong className="font-medium">连接端口</strong>在无线调试主页面长期不变；
        两个端口都必须正确才能配对成功。
      </p>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      className={clsx(
        'flex-1 rounded px-2 py-1.5 inline-flex items-center justify-center gap-1.5 transition-colors',
        active
          ? 'bg-bg text-ink shadow-sm'
          : 'text-ink-muted hover:text-ink'
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * Merged "QR pair" panel (May 2026). Replaces the old `QuickStartPanel`
 * (USB push) + `ManualStartPanel` (no-USB) — they always converged on
 * the same QR scan, the difference was just whether the server pushed
 * the 「通过二维码配对」 intent to a USB phone first. The push is now
 * an optional control inside the same panel:
 *
 *   - The QR is **already on screen** by the time this panel renders
 *     (auto-generated by `Body`'s mount effect), so the user sees
 *     something to scan immediately even before reading the help.
 *   - If a USB phone is picked, the "推送扫码页" button asks the
 *     server to `am start` the pair page on that phone.
 *   - "换一张 QR" regenerates without pushing — useful when the
 *     existing QR expired or the user wants to scan again from a
 *     phone that isn't currently USB-tethered.
 *
 * Switching the picked USB device automatically re-generates the QR
 * (handled by `Body`'s `prevLaunchOnSerialRef` effect) so the user
 * doesn't keep scanning a QR that targets the wrong phone's intent.
 */
function QrPairPanel({
  usbDevices,
  selected,
  onSelect,
  onPushPairPage,
  onRefreshQr,
}: {
  usbDevices: Array<{ serial: string; model?: string; product?: string }>;
  selected: string;
  onSelect: (v: string) => void;
  onPushPairPage: () => void;
  onRefreshQr: () => void;
}) {
  const hasUsb = usbDevices.length > 0;
  return (
    <div className="space-y-3">
      <ol className="list-decimal space-y-1 pl-5 text-xs text-ink">
        <li>
          手机进入 <span className="font-mono">设置 → 开发者选项 → 无线调试</span>（先打开开关），
          然后点 <span className="font-mono">通过二维码配对设备</span>。
        </li>
        <li>
          用手机扫描下方二维码即可。<span className="text-ink-dim">（QR 已自动生成，切换目标手机后会刷新）</span>
        </li>
      </ol>
      <div>
        <label className="mb-1 block text-[11px] uppercase tracking-wide text-ink-dim">
          目标 USB 设备（可选 — 用来比较 Wi-Fi 网络 / 推送扫码页）
        </label>
        {!hasUsb ? (
          <div className="rounded-md border border-line bg-bg-soft px-3 py-2 text-xs text-ink-muted">
            <CircleAlert className="mr-1 inline h-3.5 w-3.5" />
            没有可用的 USB 设备 — 不影响扫码配对，但无法自动推送扫码页 / 校验同网段。
          </div>
        ) : (
          <select
            className="select w-full text-xs"
            value={selected}
            onChange={(e) => onSelect(e.target.value)}
          >
            {usbDevices.map((d) => (
              <option key={d.serial} value={d.serial}>
                {d.model || d.product || d.serial} ({d.serial})
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="flex gap-2">
        <button
          className="btn-secondary flex-1"
          disabled={!hasUsb || !selected}
          onClick={onPushPairPage}
          title={hasUsb ? '通过 adb 远程拉起所选手机的「通过二维码配对」页' : '没有可用的 USB 设备'}
        >
          <Smartphone className="h-4 w-4" /> 推送扫码页到所选手机
        </button>
        <button
          className="btn-ghost"
          onClick={onRefreshQr}
          title="重新生成 QR（不再推扫码页）"
        >
          <RefreshCw className="h-4 w-4" /> 换一张 QR
        </button>
      </div>
      <p className="text-[11px] text-ink-dim">
        提示：部分定制系统（小米 / vivo / 华为等）可能拦截推扫码页的 intent — 若 4 秒后手机没自动跳转，
        手动打开「开发者选项 → 无线调试 → 通过二维码配对」也能扫。如果 mDNS 完全不通（公司 Wi-Fi 客户端隔离
        / IGMP snooping），切到「手动输入」用 host:port 直连。
      </p>
    </div>
  );
}

function SessionPanel({
  session,
  usbDevices,
  launchOnSerial,
  onSelectLaunchSerial,
  onPushPairPage,
  onRefreshQr,
  onSwitchToManual,
  onCancel,
  onClose,
}: {
  session: PairingSession;
  usbDevices: Array<{ serial: string; model?: string; product?: string }>;
  launchOnSerial: string;
  onSelectLaunchSerial: (v: string) => void;
  onPushPairPage: () => void;
  onRefreshQr: () => void;
  onSwitchToManual: () => void;
  onCancel: () => void;
  onClose: () => void;
}) {
  const terminal = isTerminal(session.state);
  const ok = session.state === 'connected';
  // Server fired the 8s silence watcher and saw no `_adb-tls-pairing._tcp`
  // adverts. Render an inline amber callout with a 1-click pivot to
  // 手动输入 — keeps the QR visible (camera might still race) but
  // makes the dead-end obvious.
  const sawSilence =
    !ok &&
    session.state !== 'cancelled' &&
    session.trace?.some((e) => e.stage === 'mdns_silence');
  // QR controls only make sense for the QR flow (synthetic manual
  // sessions don't have a `qrDataUrl`) AND while the QR is still
  // pending — once we're past `awaiting_scan` the QR has been used
  // and re-generating it would lose any in-flight pair attempt.
  const showQrControls =
    !!session.qrDataUrl &&
    !ok &&
    (session.state === 'awaiting_scan' || session.state === 'failed');
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 rounded-md border border-line bg-bg-soft px-3 py-2 text-xs">
        <StateIcon state={session.state} />
        <div className="min-w-0 flex-1">
          <div className="font-medium">{stateLabel(session.state)}</div>
          {session.error && (
            <div className="mt-0.5 truncate text-rose-600 dark:text-rose-400">{session.error}</div>
          )}
        </div>
        {!terminal && (
          <button className="btn-ghost text-xs" onClick={onCancel}>
            <RefreshCw className="h-3 w-3" /> 取消重来
          </button>
        )}
      </div>

      {!ok && session.qrDataUrl && (
        <div className="flex flex-col items-center gap-2">
          <img
            src={session.qrDataUrl}
            alt="ADB pairing QR"
            className="h-60 w-60 rounded border border-line bg-white p-2"
          />
          <div className="text-[11px] text-ink-muted">
            配对码 · pairing code
            <span className="ml-2 rounded bg-bg-soft px-2 py-0.5 font-mono text-base tracking-widest text-ink">
              {session.pairCode}
            </span>
          </div>
          <p className="text-[11px] text-ink-dim">服务 {session.service.name}</p>
        </div>
      )}

      {sawSilence && (
        <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
          <div className="flex items-start gap-2">
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-medium">扫码可能行不通 — 路由器似乎拦了 mDNS</div>
              <p className="mt-0.5 leading-relaxed">
                服务器扫了 8 秒一条 <code className="font-mono">_adb-tls-pairing._tcp</code> 广播都没收到。
                公司 Wi-Fi 常见的「客户端隔离 / IGMP snooping」会丢掉跨设备的 multicast 包 —
                即使你扫了 QR 也建立不了配对。建议改用「手动输入」：从手机
                <span className="font-mono">开发者选项 → 无线调试</span> 主页读「IP 与端口」(connect port) +
                「通过配对码配对设备」里的端口和 6 位配对码。
              </p>
            </div>
          </div>
          <button
            className="btn-secondary w-full text-xs"
            onClick={onSwitchToManual}
            title="取消当前 QR session 并切到手动输入 tab"
          >
            <KeyRound className="h-3.5 w-3.5" /> 切到「手动输入」
          </button>
        </div>
      )}

      {/*
        Inline picker + push/refresh buttons. Lets the user switch
        target phone or re-roll the QR without leaving the session
        view, so 一键拉起 失败 → 手动扫码 is just "scan the QR that's
        already shown" rather than navigating back to a different tab.
      */}
      {showQrControls && (
        <div className="space-y-2 rounded-md border border-line bg-bg-soft/40 px-3 py-2 text-[11px]">
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-ink-muted">推送扫码页到</label>
            {usbDevices.length === 0 ? (
              <span className="text-ink-dim">无 USB 设备 — 只能用上方 QR 手动扫</span>
            ) : (
              <select
                className="select min-w-[12rem] flex-1 text-[11px]"
                value={launchOnSerial}
                onChange={(e) => onSelectLaunchSerial(e.target.value)}
              >
                {usbDevices.map((d) => (
                  <option key={d.serial} value={d.serial}>
                    {d.model || d.product || d.serial} ({d.serial})
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex gap-2">
            <button
              className="btn-secondary flex-1 text-xs"
              disabled={usbDevices.length === 0 || !launchOnSerial}
              onClick={onPushPairPage}
              title="通过 adb 重新推送 am start 给所选手机"
            >
              <Smartphone className="h-3.5 w-3.5" /> 推送扫码页
            </button>
            <button
              className="btn-ghost text-xs"
              onClick={onRefreshQr}
              title="生成新 QR（不再推扫码页）"
            >
              <RefreshCw className="h-3.5 w-3.5" /> 换一张 QR
            </button>
          </div>
        </div>
      )}

      {session.launch && (
        <div className="rounded-md border border-line px-3 py-2 text-[11px]">
          <div className="font-medium text-ink-muted">
            <Smartphone className="mr-1 inline h-3 w-3" />
            一键打开扫码页：
            {session.launch.ok ? (
              <span className="ml-1 text-emerald-600 dark:text-emerald-400">
                已拉起 {session.launch.activity}
              </span>
            ) : session.launch.fallback?.ok ? (
              <span className="ml-1 text-sky-600 dark:text-sky-400">
                厂商未开放扫码页 — 已打开
                {session.launch.fallback.kind === 'developer_options'
                  ? '【开发者选项】'
                  : '【无线和网络设置】'}
                ，请进入「无线调试 → 通过二维码配对」
              </span>
            ) : (
              <span className="ml-1 text-amber-600 dark:text-amber-400">
                未能自动打开（尝试了 {session.launch.attempts.length} 个候选）；请手动打开「开发者选项 → 无线调试 → 通过二维码配对」
              </span>
            )}
          </div>
        </div>
      )}

      {ok && session.connect && (
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 px-3 py-3 text-xs">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
            <div className="min-w-0 flex-1">
              <div className="font-medium">已连接 · Connected</div>
              <div className="mt-0.5 truncate font-mono text-[11px] text-ink-muted">
                {session.connect.serial}
              </div>
              <p className="mt-1 text-[11px] text-ink-dim">
                设备已加入「我的设备 · Private」列表，所有权归你。
              </p>
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <button className="btn-primary" onClick={onClose}>
              完成
            </button>
          </div>
        </div>
      )}

      {/* Diagnostic trace strip — always rendered when we have entries
          so the user (and we, when triaging) can see exactly which
          step is hung / failed. */}
      {session.trace && session.trace.length > 0 && (
        <TraceStrip entries={session.trace} />
      )}
    </div>
  );
}

/** Vertical stage strip of per-session pairing trace entries. */
function TraceStrip({ entries }: { entries: PairingTraceEntry[] }) {
  return (
    <details
      open
      className="rounded-md border border-line bg-bg-soft/40 px-3 py-2 text-[11px]"
    >
      <summary className="cursor-pointer select-none text-ink-muted">
        诊断日志 · {entries.length} 条
      </summary>
      <ol className="mt-2 space-y-0.5">
        {entries.map((e, i) => (
          <li key={i} className="flex items-start gap-2 leading-tight">
            <span className="w-10 shrink-0 font-mono text-ink-dim">
              {fmtMs(e.ts, i === 0 ? null : entries[0].ts)}
            </span>
            <span
              className={clsx(
                'shrink-0 rounded px-1.5 py-0.5 font-mono text-[10px]',
                stageBadgeCls(e.stage)
              )}
              title={e.stage}
            >
              {stageLabel(e.stage)}
            </span>
            <span className="min-w-0 flex-1 break-all text-ink">{e.message}</span>
          </li>
        ))}
      </ol>
    </details>
  );
}

function fmtMs(ts: number, base: number | null): string {
  if (base === null) return '+0ms';
  const d = ts - base;
  if (d < 1000) return `+${d}ms`;
  return `+${(d / 1000).toFixed(1)}s`;
}

function stageLabel(stage: PairingTraceStage): string {
  switch (stage) {
    case 'session_start': return 'init';
    case 'qr_rendered': return 'qr';
    case 'pair_page_launch': return 'launch';
    case 'pair_page_launch_failed': return 'launch✕';
    case 'mdns_silence': return 'mdns·silent';
    case 'mdns_pair_up': return 'mdns·pair';
    case 'adb_pair_start': return 'pair…';
    case 'adb_pair_ok': return 'pair✓';
    case 'adb_pair_failed': return 'pair✕';
    case 'mdns_connect_wait': return 'mdns·wait';
    case 'mdns_connect_up': return 'mdns·conn';
    case 'mdns_connect_timeout': return 'mdns·to';
    case 'adb_connect_start': return 'conn…';
    case 'adb_connect_ok': return 'conn✓';
    case 'adb_connect_failed': return 'conn✕';
    case 'getprop_failed': return 'prop✕';
    case 'registry_record_ok': return 'saved';
    case 'ttl_expired': return 'expired';
    case 'cancelled': return 'cancel';
    default: return stage;
  }
}

function stageBadgeCls(stage: PairingTraceStage): string {
  if (
    stage === 'adb_pair_failed' ||
    stage === 'adb_connect_failed' ||
    stage === 'pair_page_launch_failed' ||
    stage === 'getprop_failed' ||
    stage === 'ttl_expired'
  ) {
    return 'bg-rose-500/15 text-rose-700 dark:text-rose-300';
  }
  if (
    stage === 'adb_pair_ok' ||
    stage === 'adb_connect_ok' ||
    stage === 'registry_record_ok' ||
    stage === 'mdns_connect_up'
  ) {
    return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  }
  if (stage === 'mdns_connect_timeout' || stage === 'mdns_silence') {
    return 'bg-amber-500/15 text-amber-700 dark:text-amber-300';
  }
  return 'bg-bg-card text-ink-muted border border-line';
}

function StateIcon({ state }: { state: WirelessPairState }) {
  if (state === 'connected') return <CheckCircle2 className="h-5 w-5 text-emerald-500" />;
  if (state === 'failed' || state === 'cancelled' || state === 'expired')
    return <CircleAlert className="h-5 w-5 text-rose-500" />;
  return <Loader2 className="h-5 w-5 animate-spin text-accent" />;
}

function stateLabel(state: WirelessPairState): string {
  switch (state) {
    case 'awaiting_scan':
      return '等待扫码 · 用手机扫描右侧二维码';
    case 'pairing':
      return '正在配对 · adb pair 进行中';
    case 'paired':
      return '配对完成 · 正在自动建立长连接';
    case 'connected':
      return '已连接 · 设备已上线';
    case 'cancelled':
      return '已取消';
    case 'expired':
      return '会话超时 · 请重新开始';
    case 'failed':
      return '失败';
  }
}

function isTerminal(state: WirelessPairState): boolean {
  return (
    state === 'connected' ||
    state === 'failed' ||
    state === 'cancelled' ||
    state === 'expired'
  );
}

/**
 * Network panel above the tabs. Shows:
 *   - Hub side: CIDR + interface + SSID (when detectable). SSID
 *     detection breaks on macOS Sonoma+ (Apple redacts it without a
 *     Location entitlement). When detection fails the user can click
 *     the pencil and type the SSID once — 我们把它写到 B1 prefs.global.hubSsid
 *     里，跟账号走、跨电脑同步。
 *   - Phone side (when a USB device is selected): CIDR + iface + SSID
 *     pulled from `adb shell dumpsys wifi`.
 *
 * Banner colour depends on the strongest signal we have:
 *   - Both SSIDs known and equal  → green ("same Wi-Fi, scan away")
 *   - Subnets overlap             → green (likely same Wi-Fi)
 *   - Subnets disjoint            → amber (mDNS won't cross)
 *   - Insufficient info           → plain
 */

/**
 * Proactive mDNS reachability banner. Fires a single 3 s probe when
 * the modal mounts (and re-probes on demand via the inline button).
 * Three states:
 *
 *   - **probing**: tiny inline spinner, no scary copy yet — most
 *     office Wi-Fi answers within ~500 ms so the user usually never
 *     sees this state.
 *   - **alive**: emerald compact line "mDNS 通畅 · 扫码配对可用"
 *     (only shown for ~3 s then auto-collapsed so it doesn't clutter
 *     the modal).
 *   - **silent**: amber callout with a one-click 「切到手动输入」
 *     button. This is the case that matters: corporate Wi-Fi with
 *     client-isolation / IGMP-snooping → scanning won't work no
 *     matter what we do, and we can save the user a 5-minute TTL
 *     stare-down by surfacing the dead-end immediately.
 *
 * Lives ABOVE the tabs so it's visible whether the user is about to
 * scan or about to type — `silent` still matters in the manual flow
 * because it confirms the network really is hostile and the user
 * isn't just impatient.
 */
function MdnsHealthBanner({
  showSwitch,
  onSwitchToManual,
}: {
  showSwitch: boolean;
  onSwitchToManual: () => void;
}) {
  const probe = useQuery({
    queryKey: ['wireless.mdns-probe'],
    queryFn: () => wirelessApi.probeMdns(3000),
    staleTime: 60_000,
    // The probe is cheap (re-uses the long-lived browser socket) but
    // not free — don't refetch on focus / interval, only on manual
    // refetch via the inline button.
    refetchOnWindowFocus: false,
  });
  // Collapse the green "alive" banner after a short delay so it
  // doesn't crowd the modal — failure stays sticky because it's the
  // case the user needs to act on.
  const [collapsedAlive, setCollapsedAlive] = useState(false);
  useEffect(() => {
    if (probe.data?.alive) {
      const t = window.setTimeout(() => setCollapsedAlive(true), 2500);
      return (): void => window.clearTimeout(t);
    }
    setCollapsedAlive(false);
    return undefined;
  }, [probe.data?.alive, probe.data?.sawAtMs]);

  if (probe.isLoading) {
    return (
      <div className="flex items-center gap-1.5 rounded-md border border-line bg-bg-soft px-3 py-1.5 text-[11px] text-ink-muted">
        <Loader2 className="h-3 w-3 animate-spin" /> 检测 LAN mDNS 可达性…（最多 3 秒）
      </div>
    );
  }
  if (probe.isError || !probe.data) {
    // Don't block the user with an error toast — the probe is a hint,
    // not a gate. Silent fallback is fine.
    return null;
  }
  if (probe.data.alive) {
    if (collapsedAlive) return null;
    return (
      <div className="flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-1.5 text-[11px] text-emerald-700 dark:text-emerald-400">
        <Check className="h-3 w-3" />
        mDNS 通畅 · 扫码配对可用{' '}
        <span className="text-ink-dim">
          ({probe.data.knownServices} 个服务在线{probe.data.sawAtMs ? '' : '，缓存命中'})
        </span>
      </div>
    );
  }
  return (
    <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">
      <div className="flex items-start gap-2">
        <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-medium">LAN 没有 mDNS 流量 — 扫码配对大概率失败</div>
          <p className="mt-0.5 leading-relaxed">
            服务器扫了 {Math.round(probe.data.windowMs / 1000)} 秒一条 mDNS 广播都没收到。
            常见原因：公司 Wi-Fi 开了「客户端隔离 / AP isolation / IGMP snooping」，
            或 Mac 上有 VPN 抢了默认路由。
            <strong className="font-medium">建议直接用「手动输入」</strong>
            从手机 <span className="font-mono">开发者选项 → 无线调试</span> 主页读 IP + 端口 + 配对码。
          </p>
        </div>
      </div>
      <div className="flex gap-2">
        {showSwitch && (
          <button
            className="btn-secondary flex-1 text-xs"
            onClick={onSwitchToManual}
            title="切换到「手动输入」tab"
          >
            <KeyRound className="h-3.5 w-3.5" /> 切到「手动输入」
          </button>
        )}
        <button
          className="btn-ghost text-xs"
          onClick={() => void probe.refetch()}
          disabled={probe.isFetching}
          title="重新探测一次"
        >
          {probe.isFetching ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          重新检测
        </button>
      </div>
    </div>
  );
}

function NetworkContextPanel({ usbSerial }: { usbSerial?: string }) {
  const hub = useQuery({
    queryKey: ['network.local'],
    queryFn: () => networkApi.local(),
    staleTime: 30_000,
  });
  const phone = useQuery({
    queryKey: ['device.network', usbSerial],
    queryFn: () => devicesApi.network(usbSerial!),
    enabled: !!usbSerial,
    staleTime: 15_000,
    retry: false,
  });

  // Manual hub-SSID fallback (B1 prefs.global.hubSsid). 跟账号走，跨电脑同步。
  // We prefer the server-detected SSID when present (Linux / older macOS /
  // future entitlement) and only fall back to this user-typed value when
  // detection failed.
  const manualHubSsid = useGlobalPrefs((g) => g.hubSsid ?? '');
  const updatePrefs = useGlobalPrefsUpdate();
  const [editingSsid, setEditingSsid] = useState(false);
  const [draftSsid, setDraftSsid] = useState('');

  const hubInfo = hub.data?.primary;
  const phoneInfo = phone.data?.primary;
  const detectedHubSsid = hubInfo?.ssid?.trim();
  const effectiveHubSsid = (detectedHubSsid && detectedHubSsid.length > 0) ? detectedHubSsid : manualHubSsid.trim();
  const phoneSsid = phone.data?.ssid?.trim();

  // Banner verdict. SSID equality > subnet overlap > unknown.
  const verdict = useMemo<'same' | 'maybe' | 'cross' | 'unknown'>(() => {
    if (effectiveHubSsid && phoneSsid && effectiveHubSsid === phoneSsid) return 'same';
    if (hubInfo && phoneInfo) {
      return sameSubnet(hubInfo.cidr, phoneInfo.ipv4) ? 'maybe' : 'cross';
    }
    // Hub known, no phone yet (manual tab, or USB pick still empty):
    // we have nothing to compare against — stay neutral.
    return 'unknown';
  }, [effectiveHubSsid, phoneSsid, hubInfo, phoneInfo]);

  const saveSsid = (): void => {
    const v = draftSsid.trim().slice(0, 64);
    updatePrefs({ hubSsid: v.length > 0 ? v : null });
    setEditingSsid(false);
  };

  // Hide when we know nothing and there's no phone to probe — saves
  // some vertical space when the user just opened the modal.
  if (!hub.data && !phone.data && !phone.isLoading) {
    return null;
  }

  const borderCls =
    verdict === 'cross'
      ? 'border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300'
      : verdict === 'same' || verdict === 'maybe'
        ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
        : 'border-line bg-bg-soft text-ink-muted';

  return (
    <div className={clsx('space-y-1 rounded-md border px-3 py-2 text-[11px]', borderCls)}>
      <div className="flex items-start gap-1.5">
        <Wifi className="mt-0.5 h-3 w-3 shrink-0 text-accent" />
        <div className="min-w-0 flex-1">
          <span className="font-medium text-ink">中心服务器 · Hub</span>
          {hubInfo ? (
            <>
              {' '}
              <span className="font-mono">{hubInfo.cidr}</span>
              <span className="ml-1 text-ink-dim">({hubInfo.name})</span>
            </>
          ) : hub.isLoading ? (
            <span> 读取中…</span>
          ) : (
            <span> 未检测到 Wi-Fi 网卡</span>
          )}
          {/*
            SSID surface. Detected value wins, then manual fallback,
            then a tiny "添加" affordance so the user can pin the
            SSID themselves when macOS redacts.
          */}
          <div className="mt-0.5 flex items-center gap-1">
            <span className="text-ink-dim">Wi-Fi</span>
            {editingSsid ? (
              <>
                <input
                  autoFocus
                  className="h-5 flex-1 rounded border border-line bg-bg-card px-1.5 font-mono text-[11px]"
                  value={draftSsid}
                  placeholder="输入当前连接的 Wi-Fi 名称"
                  onChange={(e) => setDraftSsid(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveSsid();
                    else if (e.key === 'Escape') setEditingSsid(false);
                  }}
                  maxLength={64}
                />
                <button className="btn-ghost !px-1" onClick={saveSsid} title="保存">
                  <Check className="h-3 w-3" />
                </button>
                <button
                  className="btn-ghost !px-1"
                  onClick={() => setEditingSsid(false)}
                  title="取消"
                >
                  <X className="h-3 w-3" />
                </button>
              </>
            ) : effectiveHubSsid ? (
              <>
                <span className="font-mono text-ink">"{effectiveHubSsid}"</span>
                {!detectedHubSsid && (
                  <span className="text-ink-dim" title="手动设置（macOS 不再开放 SSID 读取）">
                    · 手填
                  </span>
                )}
                <button
                  className="btn-ghost !px-1 text-ink-dim"
                  onClick={() => {
                    setDraftSsid(manualHubSsid);
                    setEditingSsid(true);
                  }}
                  title="修改本机记住的 Wi-Fi 名称"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              </>
            ) : (
              <button
                className="rounded border border-dashed border-line/60 px-1.5 py-0 text-[10px] text-ink-dim hover:border-accent/60 hover:text-accent"
                onClick={() => {
                  setDraftSsid('');
                  setEditingSsid(true);
                }}
                title="macOS 不再开放 SSID 读取，请手动告诉我们连的是哪个 Wi-Fi"
              >
                添加 SSID
              </button>
            )}
          </div>
        </div>
      </div>
      {usbSerial && (
        <div className="flex items-start gap-1.5">
          <Smartphone className="mt-0.5 h-3 w-3 shrink-0" />
          <div className="min-w-0 flex-1">
            <span className="font-medium text-ink">目标手机</span>
            {phoneInfo ? (
              <>
                {' '}
                <span className="font-mono">{phoneInfo.cidr}</span>
                <span className="ml-1 text-ink-dim">({phoneInfo.iface})</span>
                {phoneSsid && (
                  <span className="ml-1">
                    Wi-Fi <span className="font-mono text-ink">"{phoneSsid}"</span>
                  </span>
                )}
              </>
            ) : phone.isLoading ? (
              <span> 读取中…</span>
            ) : phone.isError ? (
              <span className="text-ink-dim"> 读取失败（手机可能未连 Wi-Fi）</span>
            ) : (
              <span className="text-ink-dim"> 未识别到 Wi-Fi 地址</span>
            )}
          </div>
        </div>
      )}
      {/* Verdict line */}
      {verdict === 'same' && (
        <div className="pt-1 font-medium">
          ✅ 同一个 Wi-Fi（SSID 一致）— 可以直接扫码 ADB 无线连接。
        </div>
      )}
      {verdict === 'maybe' && (
        <div className="pt-1 font-medium">
          ✅ 同一网段 — mDNS 通常能跨设备，可以尝试扫码。
          {!effectiveHubSsid && '（建议手填 Wi-Fi 名称，下次能直接判断同 Wi-Fi）'}
        </div>
      )}
      {verdict === 'cross' && (
        <div className="pt-1 font-medium">
          ⚠️ 跨网段：手机和服务器不在同一个 Wi-Fi。mDNS 通常不能跨子网，扫码大概率超时。
          请把手机切到与服务器相同的 Wi-Fi。
        </div>
      )}
    </div>
  );
}

/**
 * Tiny CIDR membership check (hub's `/24` etc. vs phone's bare IP).
 * Returns `true` when we can't decide — better to undercount false
 * mismatches than spam warnings on parsing edge cases.
 */
function sameSubnet(hubCidr: string, phoneIp: string): boolean {
  const m = /^(\d+\.\d+\.\d+\.\d+)\/(\d+)$/.exec(hubCidr);
  if (!m) return true;
  const baseInt = ipToInt(m[1]);
  const phoneInt = ipToInt(phoneIp);
  if (baseInt === null || phoneInt === null) return true;
  const prefix = Math.max(0, Math.min(32, parseInt(m[2], 10)));
  if (prefix === 0) return true;
  const mask = prefix === 32 ? 0xffffffff : (~0 << (32 - prefix)) >>> 0;
  return (baseInt & mask) === (phoneInt & mask);
}

function ipToInt(s: string): number | null {
  const parts = s.split('.').map((p) => parseInt(p, 10));
  if (parts.length !== 4) return null;
  if (parts.some((p) => Number.isNaN(p) || p < 0 || p > 255)) return null;
  return (((parts[0] << 24) >>> 0) | (parts[1] << 16) | (parts[2] << 8) | parts[3]) >>> 0;
}

